import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import theme, { rem } from '../../utils/theme'
import AlertMessage from '../AlertMessage'
import ButtonWithLink from '../ButtonWithLink'
import InputField from '../InputField'

const StyledPromoCode = styled.div`
  width: 100%;
  border-top: ${theme.borders.default};
  padding: ${rem(30)} 0 0;
`

const StyledPromoCodeEntry = styled.div`
  width: 100%;
  border: 1px solid ${theme.status.infoBorder};
  border-radius: 4px;
  padding: ${rem(10)} ${rem(14)} ${rem(16)};
  margin: 0 0 ${rem(30)};
`

const StyledTitle = styled.div`
  font-size: ${theme.typography.fontSizeSm};
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};
  margin-bottom: ${rem(8)};
`

const StyledAppliedCodes = styled.div`
  margin: 0 0 ${rem(9)};
`

const StyledAppliedCode = styled.div`
  display: flex;

  .success-message {
    padding: ${rem(8)} ${rem(16)};
  }
`

const StyledAppliedCodeMessage = styled(AlertMessage)`
  flex: 1;
  padding: ${rem(8)} ${rem(16)};
  background-color: transparent !important;
`

const StyledAppliedCodeCode = styled.div`
  font-size: ${theme.typography.fontSizeTinyish};
  margin-bottom: ${rem(-2)};
`

const StyledAppliedCodeLabel = styled.div`
  font-size: ${theme.typography.fontSizeTinyish};
  color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};
`

const StyledInputs = styled.div`
  display: flex;
  align-items: flex-end;

  ${theme.breakpoints.down('xs')} {
    flex-flow: row wrap;
  }
`

const StyledInput = styled.div`
  ${theme.breakpoints.down('xs')} {
    width: 100%;

    & + & {
      margin-top: ${rem(8)};
    }
  }

  ${theme.breakpoints.up('sm')} {
    flex: 1;

    & + & {
      margin-left: ${rem(8)};
    }
  }

  .content-wrapper {
    padding: 0 !important;
  }

  ${props =>
    props.hasError &&
    css`
      .label,
      input {
        color: ${theme.status.errorColor} !important;
      }
      .custom-textfield {
        border-color: ${theme.status.errorBorder} !important;
      }
    `}
`

const StyledButton = styled.div`
  ${theme.breakpoints.down('xs')} {
    width: 100%;
    margin-top: ${rem(10)};
  }

  ${theme.breakpoints.up('sm')} {
    margin-left: ${rem(8)};
  }
`

const StyledError = styled.div`
  font-size: ${theme.typography.fontSizeTinyish};
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${theme.status.errorColor};
  margin-top: ${rem(10)};
`

const PromoCode = ({
  value = '',
  disableInputField = false,
  disabledButton = false,
  isLoading = false,
  error = null,
  appliedCodes = [],
  onAddCode,
  onRemoveCode,
  buttonColour,
  buttonTextColour,
}) => {
  const [promoCodeValue, setPromoCodeValue] = useState(value || '')

  useEffect(() => {
    if (!value !== promoCodeValue) {
      setPromoCodeValue(value)
    }
  }, [value])

  const handleAddCode = () => {
    onAddCode(promoCodeValue)
  }

  const handleRemoveCode = id => {
    if (disableInputField) {
      return
    }

    onRemoveCode(id)
  }

  return (
    <StyledPromoCode>
      {appliedCodes.length > 0 && (
        <StyledAppliedCodes>
          {appliedCodes.map(appliedCode => {
            const {
              code: { id, codeValue },
              rule: { name },
            } = appliedCode
            return (
              <StyledAppliedCode key={id}>
                <StyledAppliedCodeMessage
                  variant="success"
                  show
                  showDelete={!disableInputField}
                  onClickDelete={() => handleRemoveCode(id)}
                >
                  <StyledAppliedCodeCode>{codeValue}</StyledAppliedCodeCode>
                  <StyledAppliedCodeLabel>{name} Successfully Applied</StyledAppliedCodeLabel>
                </StyledAppliedCodeMessage>
              </StyledAppliedCode>
            )
          })}
        </StyledAppliedCodes>
      )}
      <StyledPromoCodeEntry>
        <StyledTitle>Have a promo code?</StyledTitle>
        <StyledInputs>
          <StyledInput hasError={!!error}>
            <InputField
              label="Promo Code"
              placeholder="Please enter..."
              fullWidth
              disabled={disableInputField}
              onChange={e => {
                e.target.value = e.target.value ? e.target.value.toUpperCase() : e.target.value
                setPromoCodeValue(e.target.value)
              }}
              value={promoCodeValue || ''}
              hasError={!!error}
              isClearable
              handleOnClear={() => setPromoCodeValue('')}
              withBorder
              dataTestId="promoCodeField"
            />
          </StyledInput>
          <StyledButton>
            <ButtonWithLink
              onClick={handleAddCode}
              buttoncolour={buttonColour}
              buttontextcolour={buttonTextColour}
              disabled={!promoCodeValue || disabledButton || isLoading || disableInputField}
              label="Apply"
              data-testid="applyPromoCodeField"
            />
          </StyledButton>
        </StyledInputs>
        {error ? <StyledError>{error}</StyledError> : null}
      </StyledPromoCodeEntry>
    </StyledPromoCode>
  )
}

export default PromoCode
